import dayjs from 'dayjs'
import { TimeRange } from 'features/systemLens'
import {
  BaseSensorEntry,
  ChartTimeUnit,
  DateMode,
  StandaloneChart,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'

export const loadSensorsFromChart = (
  chart: StandaloneChart,
  sensorEntries: BaseSensorEntry[]
): BaseSensorEntry[] => {
  const sensorIdsFromChart = (chart.sourceConfigs ?? [])
    .filter((sc) => sc.type === 'sensor')
    .map((sc) => sc.sourceId)

  if (sensorIdsFromChart.length > 0) {
    return sensorEntries.filter((se) => sensorIdsFromChart.includes(se.id))
  }
  return []
}

export const loadDerivedValuesFromChart = (
  chart: StandaloneChart,
  derivedValueEntries: TimeSeriesDerivedValueEntryJSON[]
): TimeSeriesDerivedValueEntryJSON[] => {
  const derivedValueIdsFromChart = (chart.sourceConfigs ?? [])
    .filter((sc) => sc.type === 'derivedValue')
    .map((sc) => sc.sourceId)

  if (derivedValueIdsFromChart.length > 0 && derivedValueEntries.length > 0) {
    return derivedValueEntries.filter(
      (dv) =>
        derivedValueIdsFromChart.includes(dv.id) &&
        dv.data?.dependencyIds &&
        dv.data.dependencyIds.length > 0
    )
  }
  return []
}

export const loadSourceConfigProperties = (chart: StandaloneChart) => {
  const colors: Record<string, string> = {}
  const visibility: Record<string, boolean> = {}
  const axisRanges: Record<string, { min?: number; max?: number }> = {}
  let hasCustomRanges = false

  chart.sourceConfigs.forEach((sc) => {
    if (sc.color) {
      colors[sc.sourceId] = sc.color
    }
    if (sc.hidden) {
      visibility[sc.sourceId] = false
    }
    if (sc.yAxisRange) {
      axisRanges[sc.sourceId] = {
        min: Number(sc.yAxisRange.min.toPrecision(4)),
        max: Number(sc.yAxisRange.max.toPrecision(4)),
      }
      hasCustomRanges = true
    }
  })

  return { colors, visibility, axisRanges, hasCustomRanges }
}

interface DateConfigState {
  dateMode: DateMode
  timeRange: TimeRange
  liveTail: boolean
  relativeStart: { value?: number; unit: ChartTimeUnit }
  relativeEnd: { isNow: boolean; value?: number; unit: ChartTimeUnit }
  cadenceSeconds: number
}

export const loadDateConfigFromChart = (
  chart: StandaloneChart,
  defaultCadenceSeconds: number
): DateConfigState | null => {
  if (chart.dateConfig?.mode === 'absolute') {
    let preset: TimeRange['preset'] = 'custom'
    if (chart.dateConfig.startDate === dayjs().startOf('year').toDate()) {
      preset = 'ytd'
    }

    return {
      dateMode: 'absolute',
      timeRange: {
        from: chart.dateConfig.startDate!.toISOString(),
        to: chart.dateConfig.endDate!.toISOString(),
        preset,
      },
      liveTail: false,
      relativeStart: { value: 7, unit: 'days' },
      relativeEnd: { isNow: true, value: 0, unit: 'days' },
      cadenceSeconds: defaultCadenceSeconds,
    }
  } else if (chart.dateConfig?.mode === 'relative') {
    const { value: startValue, unit: startUnit } =
      chart.dateConfig.startDateLookback!
    const endLookBack = chart.dateConfig.endDateLookback
    const cadence =
      chart.dateConfig.updateCadenceSeconds ?? defaultCadenceSeconds

    const now = dayjs()
    const relativeEnd = endLookBack
      ? { isNow: false, value: endLookBack.value, unit: endLookBack.unit }
      : {
          isNow: true,
          value: 0,
          unit: 'days' as ChartTimeUnit,
        }

    return {
      dateMode: 'relative',
      timeRange: {
        from: now.subtract(startValue, startUnit).toISOString(),
        to: endLookBack
          ? now.subtract(endLookBack.value, endLookBack.unit).toISOString()
          : now.toISOString(),
      },
      liveTail: true,
      relativeStart: { value: startValue, unit: startUnit },
      relativeEnd,
      cadenceSeconds: cadence,
    }
  }

  return null
}
